import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/magazine/Breadcrumbs';
import ArticleCard from '../components/magazine/ArticleCard';
import { listArticles, getCategory } from '../lib/magazineApi';

export default function MagazineCategoryPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([getCategory(slug), listArticles({ category: slug, page_size: 24 })])
      .then(([cat, list]) => {
        setCategory(cat);
        setArticles(list.items || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (loading) {
    return (
      <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24">
        <p className="text-center text-neutral-500">Loading…</p>
      </main>
    );
  }
  if (error || !category) {
    return (
      <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24">
        <div className="text-center max-w-md mx-auto px-6">
          <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">404</p>
          <h1 className="mt-3 text-3xl font-bold text-neutral-900">Category not found</h1>
          <Link
            to="/magazine"
            className="inline-flex items-center gap-1 mt-6 text-[#F77F11] hover:text-[#d8651a] font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Magazine
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24" data-testid="magazine-category-page">
      <SEO
        path={`/magazine/category/${category.slug}`}
        title={`${category.name} | Kitxens Magazine`}
        description={category.description}
      />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="pt-2 pb-6">
          <Breadcrumbs
            items={[
              { label: 'Magazine', href: '/magazine' },
              { label: category.name },
            ]}
          />
        </div>

        <header className="max-w-3xl mb-12">
          <span
            className="inline-block text-[10.5px] tracking-[0.22em] font-bold uppercase"
            style={{ color: category.accent_color || '#F77F11' }}
          >
            Category
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-neutral-900 tracking-[-0.022em] leading-[1.08]">
            {category.name}
          </h1>
          {category.description && (
            <p className="mt-4 text-[16px] sm:text-[17px] text-neutral-600 leading-relaxed">
              {category.description}
            </p>
          )}
          <p className="mt-3 text-[12.5px] tracking-[0.04em] text-neutral-500">
            {category.article_count} article{category.article_count === 1 ? '' : 's'}
          </p>
        </header>

        {articles.length === 0 ? (
          <p className="text-center text-neutral-500 py-16">
            We&rsquo;re publishing this category soon.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {articles.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
